import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION_NAME = 'cars';

// Get cars by company (tenant-filtered)
export const getCarsByCompany = async (companyId, filters = {}) => {
  try {
    let constraints = [where('companyId', '==', companyId)];
    
    // Add status filter (default to active)
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    } else if (!filters.includeInactive) {
      constraints.push(where('status', '==', 'active'));
    }
    
    // Note: Complex filters like fuelType, transmission need composite indexes
    // For now, we'll filter in memory if needed
    
    const q = query(collection(db, COLLECTION_NAME), ...constraints);
    const querySnapshot = await getDocs(q);
    
    let cars = [];
    querySnapshot.forEach((doc) => {
      cars.push({ id: doc.id, ...doc.data() });
    });
    
    // Apply additional filters in memory
    if (filters.fuelType) {
      cars = cars.filter(car => car.fuelType === filters.fuelType);
    }
    if (filters.transmission) {
      cars = cars.filter(car => car.transmission === filters.transmission);
    }
    if (filters.seats) {
      cars = cars.filter(car => car.seats >= filters.seats);
    }
    if (filters.brand) {
      cars = cars.filter(car => car.brand === filters.brand);
    }
    
    // Sort by createdAt desc
    cars.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
      return dateB - dateA;
    });
    
    return { cars, error: null };
  } catch (error) {
    console.error('Error getting cars by company:', error);
    return { cars: [], error: error.message };
  }
};

// Get all cars (admin view)
export const getAllCars = async (filters = {}) => {
  try {
    let constraints = [];
    
    if (filters.companyId) {
      constraints.push(where('companyId', '==', filters.companyId));
    }
    
    const q = constraints.length > 0 
      ? query(collection(db, COLLECTION_NAME), ...constraints)
      : query(collection(db, COLLECTION_NAME));
    
    const querySnapshot = await getDocs(q);
    
    let cars = [];
    querySnapshot.forEach((doc) => {
      cars.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by createdAt desc
    cars.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
      return dateB - dateA;
    });
    
    return { cars, error: null };
  } catch (error) {
    console.error('Error getting all cars:', error);
    return { cars: [], error: error.message };
  }
};

// Get single car by ID
export const getCarById = async (carId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, carId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { 
        car: { id: docSnap.id, ...docSnap.data() }, 
        error: null 
      };
    }
    return { car: null, error: 'Car not found' };
  } catch (error) {
    console.error('Error getting car:', error);
    return { car: null, error: error.message };
  }
};

// Create car
export const createCar = async (companyId, data, createdBy) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...data,
      companyId,
      status: data.status || 'active',
      rating: 0,
      totalReviews: 0,
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { id: docRef.id, error: null };
  } catch (error) {
    console.error('Error creating car:', error);
    return { id: null, error: error.message };
  }
};

// Update car
export const updateCar = async (carId, data) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, carId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    
    return { error: null };
  } catch (error) {
    console.error('Error updating car:', error);
    return { error: error.message };
  }
};

// Toggle car status
export const toggleCarStatus = async (carId, status) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, carId);
    await updateDoc(docRef, {
      status,
      updatedAt: serverTimestamp()
    });
    return { error: null };
  } catch (error) {
    console.error('Error toggling car status:', error);
    return { error: error.message };
  }
};

// Delete car (soft delete)
export const deleteCar = async (carId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, carId);
    await deleteDoc(docRef);
    return { error: null };
  } catch (error) {
    console.error('Error deleting car:', error);
    return { error: error.message };
  }
};

// Get unique brands for filter (by company)
export const getCarBrands = async (companyId) => {
  try {
    const result = await getCarsByCompany(companyId, { includeInactive: true });
    const brands = [...new Set(result.cars.map(car => car.brand).filter(Boolean))];
    return { brands: brands.sort(), error: null };
  } catch (error) {
    console.error('Error getting car brands:', error);
    return { brands: [], error: error.message };
  }
};

// Get car stats by company
export const getCarStats = async (companyId) => {
  try {
    const result = await getCarsByCompany(companyId, { includeInactive: true });
    
    const stats = {
      total: result.cars.length,
      active: result.cars.filter(c => c.status === 'active').length,
      maintenance: result.cars.filter(c => c.status === 'maintenance').length,
      inactive: result.cars.filter(c => c.status === 'inactive').length,
    };
    
    return { stats, error: null };
  } catch (error) {
    console.error('Error getting car stats:', error);
    return { stats: null, error: error.message };
  }
};

// Get all car stats (admin)
export const getAllCarStats = async () => {
  try {
    const result = await getAllCars();
    
    const stats = {
      total: result.cars.length,
      active: result.cars.filter(c => c.status === 'active').length,
      maintenance: result.cars.filter(c => c.status === 'maintenance').length,
      inactive: result.cars.filter(c => c.status === 'inactive').length,
    };
    
    // Group by company
    const byCompany = {};
    result.cars.forEach(car => {
      if (!byCompany[car.companyId]) {
        byCompany[car.companyId] = 0;
      }
      byCompany[car.companyId]++;
    });
    
    stats.byCompany = byCompany;
    
    return { stats, error: null };
  } catch (error) {
    console.error('Error getting all car stats:', error);
    return { stats: null, error: error.message };
  }
};

// ==================== CAR BLOCKING FUNCTIONS ====================

// Create a car block (for offline bookings/maintenance)
export const createCarBlock = async (carId, companyId, data) => {
  try {
    const blocksCollection = collection(db, 'carBlocks');
    
    const blockData = {
      carId,
      companyId,
      fromDateTime: data.fromDateTime,
      toDateTime: data.toDateTime,
      reason: data.reason || 'Offline booking',
      type: data.type || 'offline_booking', // 'offline_booking', 'maintenance', 'other'
      createdBy: data.createdBy || 'manager',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(blocksCollection, blockData);
    return { id: docRef.id, error: null };
  } catch (error) {
    console.error('Error creating car block:', error);
    return { id: null, error: error.message };
  }
};

// Get car blocks
export const getCarBlocks = async (carId, dateRange = {}) => {
  try {
    let constraints = [where('carId', '==', carId)];
    
    const q = query(collection(db, 'carBlocks'), ...constraints);
    const querySnapshot = await getDocs(q);
    
    let blocks = [];
    querySnapshot.forEach((doc) => {
      blocks.push({ id: doc.id, ...doc.data() });
    });
    
    // Filter by date range if provided
    if (dateRange.from && dateRange.to) {
      blocks = blocks.filter(block => {
        const blockFrom = new Date(block.fromDateTime);
        const blockTo = new Date(block.toDateTime);
        const rangeFrom = new Date(dateRange.from);
        const rangeTo = new Date(dateRange.to);
        
        // Check for overlap
        return blockFrom < rangeTo && blockTo > rangeFrom;
      });
    }
    
    // Sort by fromDateTime
    blocks.sort((a, b) => {
      const dateA = a.fromDateTime ? new Date(a.fromDateTime).getTime() : 0;
      const dateB = b.fromDateTime ? new Date(b.fromDateTime).getTime() : 0;
      return dateA - dateB;
    });
    
    return { blocks, error: null };
  } catch (error) {
    console.error('Error getting car blocks:', error);
    return { blocks: [], error: error.message };
  }
};

// Get all blocks for a company
export const getCompanyCarBlocks = async (companyId) => {
  try {
    const q = query(
      collection(db, 'carBlocks'),
      where('companyId', '==', companyId)
    );
    const querySnapshot = await getDocs(q);
    
    let blocks = [];
    querySnapshot.forEach((doc) => {
      blocks.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by fromDateTime
    blocks.sort((a, b) => {
      const dateA = a.fromDateTime ? new Date(a.fromDateTime).getTime() : 0;
      const dateB = b.fromDateTime ? new Date(b.fromDateTime).getTime() : 0;
      return dateA - dateB;
    });
    
    return { blocks, error: null };
  } catch (error) {
    console.error('Error getting company car blocks:', error);
    return { blocks: [], error: error.message };
  }
};

// Delete car block
export const deleteCarBlock = async (blockId) => {
  try {
    const docRef = doc(db, 'carBlocks', blockId);
    await deleteDoc(docRef);
    return { error: null };
  } catch (error) {
    console.error('Error deleting car block:', error);
    return { error: error.message };
  }
};
